// ═══ SESSION DATA MODEL ════════════════════════════════════
// Conçu pour supporter les 9 combinaisons niveau×objectif
// et tous les formats : Circuit, Superset, Pyramide, Rest-Pause,
// Cluster, Tabata+AMRAP, Biset+DropSet, Myo-Reps, Testing, Deload

export type SessionFormat =
  | "circuit"
  | "superset"
  | "pyramide"
  | "rest-pause"
  | "cluster"
  | "tabata-amrap"
  | "biset-dropset"
  | "myo-reps"
  | "straight-sets"
  | "testing"
  | "deload"
  | "cardio"

// ─── Warmup ────────────────────────────────────────────────
export interface WarmupExercise {
  name: string
  reps: string // "10", "5 /côté", "15 sec"
  // UUID de `exercises.id`, présent une fois l'item migré en forme objet. Résout la
  // vidéo par identifiant plutôt que par nom — même fiabilité que le corps de séance.
  exerciseId?: string
  // Repli matériel écrit entre crochets par le programme ("Jumping Jack… si pas de
  // rameur"), affiché sous la consigne principale. Voir `splitWarmupVariant`.
  variant?: string
  videoUrl?: string
}

export interface WarmupBlock {
  label: string // "Mobilité Routine", "Dynamic Warm-Up"
  duration: string // "5 min"
  rounds?: number // 2 pour Dynamic Warm-Up
  restBetweenRounds?: number // secondes entre les rounds
  exercises: WarmupExercise[]
}

// ─── Biomechanical instructions (from exercises DB) ────────
export interface ExerciseInstructions {
  cues: string[]
  errors: string[]
  breathing?: string
}

// ─── Schéma de série (méthodes composées) ──────────────
//
// Une série droite est entièrement décrite par `sets` / `reps` / `charge`. Une méthode
// composée ne l'est pas : elle enchaîne des PALIERS qui n'ont ni les mêmes reps, ni la
// même charge, ni la même façon de passer au suivant. C'est ce que décrit `SetScheme`,
// présent uniquement sur ces exercices — son absence est ce qui dit « séries droites ».
//
// Le contenu vient du JSON du programme (`Exercise.set_scheme`), comme `test_protocol` :
// ajuster un protocole se fait là, pas dans le code. Voir `src/lib/setScheme.ts`.

/**
 * Ce que le client fait entre un palier et le suivant. C'est la seule chose qui
 * distingue vraiment les méthodes entre elles — un rest-pause et un drop set ont la
 * même allure sur le papier, mais l'un rend la barre 20 secondes et l'autre change la
 * charge sans souffler.
 */
export type PalierTransition =
  /** Micro-repos chronométré — rest-pause, cluster, myo-reps.
   *  `estimated` : la durée n'était écrite nulle part dans le programme et vient du
   *  repli par méthode. L'écran le dit, plutôt que de faire passer une convention
   *  pour une prescription. */
  | { kind: "pause"; seconds: number; estimated?: boolean }
  /** Changement de charge sans repos prescrit — drop set, pyramide montante.
   *  `deltaKg` est calculé à partir des charges résolues, jamais déclaré ; null quand
   *  au moins un des deux paliers n'est pas convertible en kg. */
  | { kind: "load-change"; deltaKg: number | null }
  /** Enchaînement immédiat sur le mouvement suivant, sans repos — bi-set. */
  | { kind: "chain" }

/** Charge d'un palier, déjà résolue et arrondie au cran de matériel. */
export interface PalierCharge {
  /** Ce qui s'affiche : "32 kg", "~8 kg par haltère", "PDC". */
  label: string
  /** Valeur numérique APRÈS arrondi matériel. null = non convertible (PDC, cardio,
   *  1RM de référence manquant). */
  kg: number | null
  /** % 1RM d'origine, AVANT arrondi. Conservé parce que c'est lui, et pas les kg
   *  arrondis, qui dit l'ordre voulu entre deux paliers. */
  percent1Rm: number | null
  basis: import("@/lib/exerciseNormalization").ChargeBasis
  perDumbbell?: boolean
}

export interface SetPalier {
  /** Rang dans la série, 1-based — l'ordre d'exécution, pas un index de tableau. */
  index: number
  /** Reps prescrites. Une chaîne, jamais un nombre : "8", "6-8", "max", "20''". */
  reps: string
  charge: PalierCharge
  /** Mouvement de ce palier, s'il change en cours de série (bi-set). Absent = même
   *  mouvement que l'exercice. */
  movement?: string
  /** Transition vers le palier suivant. Absent sur le dernier palier — c'est ce qui
   *  le désigne comme dernier. */
  transition?: PalierTransition
}

/**
 * Méthode composée.
 *
 * "drop-set" est réservé aux protocoles réellement DÉGRESSIFS (la charge baisse d'un
 * palier au suivant). Un protocole dont la charge MONTE est une pyramide montante,
 * quoi qu'en dise le libellé du programme — la règle est appliquée une fois pour
 * toutes par la migration, à partir des charges, pas des mots.
 */
export type SetSchemeMethod =
  | "rest-pause"
  | "drop-set"
  | "pyramide-montante"
  | "bi-set"
  | "cluster"
  | "myo-reps"

export interface SetScheme {
  method: SetSchemeMethod
  /** Libellé court affiché en tête du schéma : "Rest-Pause 4+3+2". */
  label: string
  /** Toujours ≥ 2. En dessous, il n'y a pas de schéma — le champ est absent. */
  paliers: SetPalier[]
}

// ─── Exercise ──────────────────────────────────────────────
export interface SessionExercise {
  order: number
  name: string // libellé brut JSON, affiché tel quel dans les listes existantes
  // Nom canonique résolu — via exercise_id (table `exercises`) en priorité,
  // sinon fallback normalizeExerciseName(name). Source de vérité pour le titre,
  // le calcul de charge, et le matching instructions/vidéo.
  canonicalName: string
  exerciseId?: string // "id1 + id2" pour un combiné — présent seulement si migré
  category: string // Polyarticulaire, Monoarticulaire, Pliométrie, Gainage, Cardio
  primaryMuscle: string
  secondaryMuscle: string
  anatomyImageUrl?: string // URL Supabase Storage (fond dark)
  videoUrl?: string // URL Supabase Storage
  // Vidéos par mouvement pour exercices combinés ("ExoA + ExoB") — une par partie
  videoUrlParts?: Array<{ name: string; url: string }>

  // Performance
  sets: string // "3", "4", "Pyramide", "3 RP", "4 Cluster", "1 set", "8 rounds"
  reps: string // "12", "8 each leg", "12-10-8-6-4-2", "3+3+3+3 (=12)", "8+4+3", "20'' max", "200m"
  tempo: string // "2010", "X0X0", "3010"
  tempoExplain: string // "2 sec descente · 0 sec iso bas · 1 sec montée · 0 sec iso haut"
  charge: string // "30 kg", "PDC", "70% 1RM" (converti en kg dans l'app)

  // Rest
  restBetweenSets: number // secondes (0 = enchaîner)
  restBetweenExercises: number // secondes
  restNature: string // "Active (marche)", "Active (sur place)", "Passive"
  restExplain: string // "Enchaîner avec l'exercice suivant sans récupération"

  // Protocole de test de force déclaré par le JSON du programme — présent seulement sur
  // les séances de test. Absent = repli sur le protocole par niveau (voir strengthTest.ts).
  testProtocol?: import("@/lib/strengthTest").TestSetSpec[]

  // Instructions spécifiques à l'exercice (notes JSON du programme)
  instructions: string[]
  // Instructions biomécaniques (table exercises, JSONB) — exercice simple
  instructionsDb?: ExerciseInstructions
  // Instructions biomécaniques pour exercices combinés ("ExoA + ExoB")
  instructionsDbParts?: Array<{ name: string; instructions: ExerciseInstructions | null }>

  // Schéma de série — présent UNIQUEMENT sur les méthodes composées (rest-pause,
  // bi-set, drop set, pyramide montante, cluster, myo-reps). Absent sur une série
  // droite, qui n'a pas de paliers : c'est cette absence qui conditionne l'affichage
  // de la ligne de paliers dans l'écran de séance.
  //
  // C'est la PRESCRIPTION, pas les charges : comme `charge`, elle se transporte telle
  // que le programme l'a écrite et ne devient des kg qu'à l'écran, où le 1RM et le
  // poids de corps sont connus — `resolveSetScheme()` la résout en `SetScheme`.
  setSchemeSpec?: NonNullable<import("@/types/programs").Exercise["set_scheme"]>

  // Groupement (supersets, bisets, dropsets)
  groupId?: string // Les exercices avec le même groupId sont réalisés ensemble
  groupLabel?: string // "Superset A", "Biset 1", "Drop Set"
}

// ─── Finisher ──────────────────────────────────────────────
export interface FinisherStep {
  label: string // "Wall Ball"
  reps: string // "10"
  charge: string // "8 kg", "PDC"
  videoUrl?: string
}

// ─── Cooldown ──────────────────────────────────────────────
export interface CooldownStep {
  // Contenu réel, tiré du texte du programme : "Marche rapide tapis (7-8 km/h)".
  // Surtout PAS "Retour au calme" — l'en-tête de section le dit déjà.
  label: string
  // Résumé court pour l'APERÇU de séance : les activités seules, sans durée ni
  // intensité ("Vélo puis Rameur"). Absent sur un texte à une seule activité, déjà
  // court — l'aperçu retombe alors sur `label`.
  summary?: string
  detail: string // texte brut du programme, tel qu'il est écrit en base
  duration: string // "10 min" — somme des durées du texte
  // Repli matériel entre crochets ("marche extérieure si pas de tapis"), affiché sous
  // la consigne. Même traitement qu'à l'échauffement, voir `splitWarmupVariant`.
  variant?: string
}

// ─── Session complète ──────────────────────────────────────
export interface SessionData {
  // Identité
  id: string // UUID Supabase
  sessionNumber: number // S1, S2, etc.
  weekNumber: number
  cycleNumber: number
  cycleName: string // "Full Body — Endurance de force"

  // Format
  format: SessionFormat
  formatLabel: string // "Circuit Training Full Body — 3 rounds"
  displayName: string // "Full Body — Circuit Training"

  // Paramètres du format
  totalRounds?: number // Pour circuit / tabata / amrap
  totalSets?: number // Pour straight-sets / rest-pause / cluster
  estimatedDuration: string // "60 min"

  // Repos inter-rounds (circuit) ou inter-exercices global
  interRoundRest?: number // secondes
  interRoundNature?: string // "Repos actif : Marche"

  // Contenu
  warmup: WarmupBlock[]
  exercises: SessionExercise[]
  finisher?: FinisherStep[] // Pas toujours présent (ex: testing, deload cardio)
  cooldown: CooldownStep[]

  // Notes
  notes?: string // "Finisher : 10 Wall Ball + 5 burpees + 1 km tapis"
}

// ─── Programme complet ─────────────────────────────────────
export interface ProgramData {
  id: string
  level: "beginner" | "intermediate" | "advanced"
  goalId: string // "seche-definition", "construction-musculaire", etc.
  goalLabel: string // "Sèche & Définition"
  location: "salle" | "domicile"
  totalWeeks: number // 12
  sessions: SessionData[]
}

// ─── Helpers ───────────────────────────────────────────────

/** Décode le tempo en explication lisible */
/**
 * Description COURTE d'un tempo, pour la tuile de l'écran de séance — générée depuis
 * le code, jamais écrite à la main par exercice.
 *
 * Les quatre positions sont descente / pause en bas / montée / pause en haut.
 *
 * Les deux TEMPS DE MOUVEMENT — descente et montée — s'affichent toujours, même à
 * 1 s : ce sont eux que le client doit contrôler, et les taire laisserait croire
 * qu'il n'y a rien à contrôler. Les deux PAUSES, elles, se taisent à 0 ou 1 s :
 * personne ne « tient » une pause d'une seconde, la mentionner serait du bruit.
 *
 * "X" veut dire explosif, et n'a de sens que sur les deux temps de mouvement. Un 0
 * sur l'un d'eux dit la même chose — aucun temps imposé, donc explosif — et se lit
 * comme un X : "0 s montée" ne veut rien dire pour personne. Cas de garde : les deux
 * temps de mouvement ne valent jamais 0 dans les programmes actuels.
 *
 *   3010 → "3 s descente · 1 s montée"
 *   30X0 → "3 s descente · montée explosive"
 *   4201 → "4 s descente · 2 s pause en bas · 1 s montée"
 *   4200 → "4 s descente · 2 s pause en bas · montée explosive"
 */
export function describeTempo(tempo: string): string {
  const code = tempo.trim().toUpperCase()
  if (code.length !== 4 || !/^[0-9X]{4}$/.test(code)) {
    // Tempo nommé ("rapide", "max", "iso"...) ou format inconnu : la forme longue
    // reste la meilleure réponse, c'est déjà une phrase.
    return decodeTempo(tempo)
  }
  const SLOTS = [
    { label: "descente",      always: true,  explosive: "descente explosive" },
    { label: "pause en bas",  always: false, explosive: null },
    { label: "montée",        always: true,  explosive: "montée explosive" },
    { label: "pause en haut", always: false, explosive: null },
  ]
  const parts: string[] = []
  code.split("").forEach((raw, i) => {
    const slot = SLOTS[i]
    const n = raw === "X" ? null : parseInt(raw, 10)
    // X, ou 0 sur un temps de mouvement : aucun temps imposé.
    if (slot.explosive && (raw === "X" || n === 0)) {
      parts.push(slot.explosive)
      return
    }
    if (n == null) return
    if (n <= 1 && !slot.always) return
    parts.push(`${n} s ${slot.label}`)
  })
  return parts.join(" · ")
}

export function decodeTempo(tempo: string): string {
  if (tempo === "rapide" || tempo === "X0X0") return "Exécution continue et explosive"
  if (tempo === "max") return "Effort maximal"
  if (tempo === "lent") return "Exécution lente et contrôlée"
  if (tempo === "modéré") return "Rythme modéré et contrôlé"
  if (tempo === "iso") return "Maintien isométrique"
  if (tempo.length === 4 && /^\d+$/.test(tempo)) {
    const [e, pb, c, pt] = tempo.split("")
    return `${e} sec descente · ${pb} sec iso bas · ${c} sec montée · ${pt} sec iso haut`
  }
  return tempo
}

/** Formate un temps en secondes en mm:ss */
export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, "0")}`
}

/** Formate le repos en label lisible */
export function formatRest(seconds: number): string {
  if (seconds === 0) return "0 sec"
  if (seconds < 60) return `${seconds} sec`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m} min ${s}s` : `${m} min`
}
